import type { LLMAnalysisResultItem, LLMMetadata } from "src/modules/ANNModule/ANNLLMAnalyzer";
import {
   emptyProvenance,
   type ANNServitutProvenance,
} from "src/modules/ANNModule/ANNServitutProvenance";
import type { ServitutFieldAnnotationMap } from "src/modules/ANNModule/ANNServitutFieldAnnotations";

type ServitutRedegoerelseRowHandling = "legacyLlmPdf" | "electronicZipPackage";
type ServitutRedegoerelseDocumentSource = "tingbog" | "annotation" | "customUpload" | "rigsarkivetRequired";

type RestorableRow = {
   handling?: ServitutRedegoerelseRowHandling;
   documentSource?: ServitutRedegoerelseDocumentSource;
   electronicServitut?: any;
   customPdf?: unknown;
   annotationRecord?: unknown;
   annotationDocument?: unknown;
   aktDocument?: string;
   aktId?: string;
   documentUrl?: string;
   pdfStatus: "pending" | "ready" | "missing" | "failed";
   pdfSource?: string;
   analysisItem?: LLMAnalysisResultItem;
   selectedPages?: number[];
   provenance?: ANNServitutProvenance;
   sourceMessage?: string;
   resultsAccepted?: boolean;
   fieldAnnotations?: ServitutFieldAnnotationMap;
   stedfaestelseAnnotation?: unknown;
   servitut?: any;
};

export function replaceRowMetadata(
   metadata: LLMMetadata[],
   rowKey: string,
   nextEntry?: LLMMetadata,
): LLMMetadata[] {
   const without = metadata.filter((entry) => entry.id !== rowKey);
   return nextEntry ? [...without, nextEntry] : without;
}

export function rowHasWorkingDocument(row: {
   customPdf?: { s3Key?: string } | unknown;
   aktId?: string;
   electronicServitut?: unknown;
}): boolean {
   if (row.customPdf && typeof row.customPdf === "object" && "s3Key" in row.customPdf) {
      return Boolean((row.customPdf as { s3Key?: string }).s3Key);
   }
   return Boolean(row.aktId || row.electronicServitut);
}

export function freshProvenanceAfterAcceptRevert(source?: "akt" | "file"): ANNServitutProvenance {
   return {
      ...emptyProvenance(),
      ...(source ? { source } : {}),
      origin: "ai",
      reviewState: "unchecked",
      aiPagesKept: [],
      fieldStatus: {
         resume: "not_run",
         entitledParty: "not_run",
         condition: "not_run",
         statement: "not_run",
         pages: "not_run",
      },
      analysisStale: false,
   };
}

function revertSourceForRow(row: RestorableRow): "akt" | "file" | undefined {
   const hasCustomPdf = row.customPdf && typeof row.customPdf === "object" && "s3Key" in row.customPdf
      ? Boolean((row.customPdf as { s3Key?: string }).s3Key)
      : false;
   if (hasCustomPdf) {
      return "file";
   }
   // Newer/digital tingbog servitutter are not scanned akts.
   if (row.handling === "electronicZipPackage") {
      return undefined;
   }
   return "akt";
}

/** After Modal A Fortryd: drop A-document and the four analysis notes; keep stedfæstelse. */
export function clearedRowAfterAcceptRevert<T extends RestorableRow>(row: T): T {
   const hasCustomPdf = row.customPdf && typeof row.customPdf === "object" && "s3Key" in row.customPdf
      ? Boolean((row.customPdf as { s3Key?: string }).s3Key)
      : false;
   const hasDocument = rowHasWorkingDocument(row);
   return {
      ...row,
      annotationRecord: undefined,
      annotationDocument: undefined,
      documentSource: row.documentSource === "rigsarkivetRequired"
         ? "rigsarkivetRequired"
         : hasCustomPdf
            ? "customUpload"
            : "tingbog",
      analysisItem: undefined,
      selectedPages: undefined,
      resultsAccepted: false,
      fieldAnnotations: {},
      sourceMessage: undefined,
      pdfStatus: hasDocument ? "ready" : "pending",
      provenance: freshProvenanceAfterAcceptRevert(revertSourceForRow(row)),
   };
}

export function needsLandRegistryFetchAfterAcceptRevert(row: RestorableRow): boolean {
   return !rowHasWorkingDocument(clearedRowAfterAcceptRevert(row));
}
